import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface RoleTestingProfile {
  name: string;
  description: string;
  roles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class RoleTestingService {
  private readonly ROLE_TESTING_KEY = 'mmaps_role_testing_profile';
  private readonly ROLE_TESTING_ENABLED_KEY = 'mmaps_role_testing_enabled';

  // Predefined role testing profiles
  private readonly testingProfiles: RoleTestingProfile[] = [
    {
      name: 'no-roles',
      description: 'No Roles (Approver not in group)',
      roles: [],
    },
    {
      name: 'gas-only',
      description: 'Gas-only user',
      roles: ['MMAPS_Gas_Users'],
    },
    {
      name: 'ets-only',
      description: 'ETS-only user',
      roles: ['MMAPS_ETS_Users'],
    },
    {
      name: 'ets-pac',
      description: 'ETS PAC user',
      roles: ['MMAPS_ETS_PAC_Users'],
    },
    {
      name: 'ets-asset',
      description: 'ETS Asset user',
      roles: ['MMAPS_ETS_Asset_Users'],
    },
    {
      name: 'ets-readonly',
      description: 'ETS Read-only user',
      roles: ['MMAPS_ETS_RO_Users'],
    },
    {
      name: 'generation-only',
      description: 'Generation-only user',
      roles: ['MMAPS_Generation_Users'],
    },
    {
      name: 'real-estate-only',
      description: 'Real Estate-only user',
      roles: ['MMAPS_Real_Estate_Users'],
    },
    {
      name: 'gas-ets',
      description: 'Gas + ETS user',
      roles: ['MMAPS_Gas_Users', 'MMAPS_ETS_Users'],
    },
    {
      name: 'all-access',
      description: 'Access to All Forms',
      roles: [
        'MMAPS_Gas_Users',
        'MMAPS_ETS_Users',
        'MMAPS_Generation_Users',
        'MMAPS_Real_Estate_Users',
      ],
    },
    {
      name: 'all-access-pac',
      description: 'All Access + PAC',
      roles: [
        'MMAPS_Gas_Users',
        'MMAPS_ETS_PAC_Users',
        'MMAPS_Generation_Users',
        'MMAPS_Real_Estate_Users',
      ],
    },
  ];

  constructor() {}

  /**
   * Check if role testing is enabled for the current environment
   */
  isRoleTestingEnabled(): boolean {
    // Only allow role testing in local and dev
    if (!environment.enableRoleTesting) {
      return false;
    }

    // Check if explicitly enabled via localStorage
    const enabled = localStorage.getItem(this.ROLE_TESTING_ENABLED_KEY);
    return enabled === 'true';
  }

  /**
   * Enable or disable role testing
   */
  setRoleTestingEnabled(enabled: boolean): void {
    if (enabled) {
      localStorage.setItem(this.ROLE_TESTING_ENABLED_KEY, 'true');
    } else {
      localStorage.removeItem(this.ROLE_TESTING_ENABLED_KEY);
      this.clearActiveProfile();
    }
  }

  /**
   * Check if role testing is available in this environment (not just enabled)
   */
  isRoleTestingAvailable(): boolean {
    //!! to ensure value is returned as boolean
    return !!environment.enableRoleTesting;
  }

  /**
   * Get all available testing profiles
   */
  getTestingProfiles(): RoleTestingProfile[] {
    return [...this.testingProfiles];
  }

  /**
   * Set the active testing profile
   */
  setActiveProfile(profileName: string): void {
    if (!this.isRoleTestingEnabled()) {
      console.warn('Role testing is not enabled');
      return;
    }

    const profile = this.testingProfiles.find((p) => p.name === profileName);
    if (profile) {
      localStorage.setItem(this.ROLE_TESTING_KEY, profileName);
      console.log(`Role testing profile set to: ${profile.description}`, profile.roles);
    } else {
      console.error(`Profile '${profileName}' not found`);
    }
  }

  /**
   * Clear the active testing profile
   */
  clearActiveProfile(): void {
    localStorage.removeItem(this.ROLE_TESTING_KEY);
  }

  /**
   * Get the currently active testing profile
   */
  getActiveProfile(): RoleTestingProfile | null {
    if (!this.isRoleTestingEnabled()) {
      return null;
    }

    const activeProfileName = localStorage.getItem(this.ROLE_TESTING_KEY);
    if (activeProfileName) {
      return this.testingProfiles.find((p) => p.name === activeProfileName) || null;
    }
    return null;
  }

  /**
   * Get the test roles that should override the actual user roles
   * Returns null if no testing profile is active
   */
  getTestRoles(): string[] | null {
    const activeProfile = this.getActiveProfile();
    return activeProfile ? activeProfile.roles : null;
  }

  /**
   * Apply role testing override if active, otherwise return original roles
   */
  applyRoleTestingOverride(originalRoles: string[]): string[] {
    const testRoles = this.getTestRoles();
    if (testRoles !== null) {
      console.log('🧪 Role Testing Active:', this.getActiveProfile().description, testRoles);
      return testRoles;
    }
    return originalRoles;
  }

  /**
   * Helper method to display current testing status in console
   */
  logTestingStatus(): void {
    if (!this.isRoleTestingEnabled()) {
      console.log('🚫 Role testing is disabled');
      return;
    }

    const activeProfile = this.getActiveProfile();
    if (activeProfile) {
      console.log(
        '🧪 Active Role Testing Profile:',
        activeProfile.description,
        activeProfile.roles
      );
    } else {
      console.log('✅ Role testing enabled but no active profile');
    }
  }

  /**
   * Browser console helper methods for easy testing
   * Call window.roleTest.help() for usage instructions
   */
  getConsoleHelpers(): any {
    const notAvailableMsg = '🚫 Role Testing is not available in this environment.';
    return {
      help: () => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        if (!this.isRoleTestingEnabled()) {
          console.log('🧪 Role testing is not enabled. Call roleTest.enable() to enable it first.');
          return;
        }
        console.log(
          '🧪 MMAPS Role Testing Console Helper\n\n' +
            'Available commands:\n' +
            '  roleTest.enable() - Enable role testing\n' +
            '  roleTest.disable() - Disable role testing\n' +
            '  roleTest.profiles() - List all available profiles\n' +
            '  roleTest.set("profile-name") - Set active profile\n' +
            '  roleTest.clear() - Clear active profile\n' +
            '  roleTest.status() - Show current status\n\n' +
            'Example: roleTest.set("all-access") then refresh page'
        );
      },
      enable: () => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        this.setRoleTestingEnabled(true);
        console.log(
          '✅ Role testing enabled. Quick options:\n' +
            '  roleTest.set("all-access") - All roles\n' +
            '  roleTest.set("no-roles") - No role\n' +
            '  roleTest.set("gas-only") - Gas\n' +
            '  roleTest.set("real-estate-only") - Real estate\n' +
            '  roleTest.set("ets-only") - ETS user\n' +
            '  roleTest.set("ets-asset") - ETS approver\n' +
            '  roleTest.set("ets-pac") - PAC user\n' +
            'Then refresh page. Use roleTest.profiles() for full list.'
        );
      },
      disable: () => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        this.setRoleTestingEnabled(false);
        console.log('🚫 Role testing disabled');
      },
      profiles: () => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        if (!this.isRoleTestingEnabled()) {
          console.log('❌ Role testing is not enabled. Call roleTest.enable() first.');
          return;
        }
        console.table(this.testingProfiles);
      },
      set: (profileName: string) => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        if (!this.isRoleTestingEnabled()) {
          console.log('❌ Role testing is not enabled. Call roleTest.enable() first.');
          return;
        }
        this.setActiveProfile(profileName);
      },
      clear: () => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        if (!this.isRoleTestingEnabled()) {
          console.log('❌ Role testing is not enabled. Call roleTest.enable() first.');
          return;
        }
        this.clearActiveProfile();
        console.log('🧹 Active profile cleared');
      },
      status: () => {
        if (!this.isRoleTestingAvailable()) {
          console.log(notAvailableMsg);
          return;
        }
        this.logTestingStatus();
      },
    };
  }
}
